import { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import PageLayout from "components/layout/PageLayout";
import { ROUTES, WORK_ORDER_STATUS } from "constants/index";
import { IMovementWorkOrderListItem } from "types";
import {
  STATUS_COLOR,
  OPERATOR_TABS,
  WORK_ORDER_STATUS_LABEL,
  WorkOrderOperatorStatus,
} from "helpers/work_orders";
import { MdLocalShipping } from "react-icons/md";
import { typographyStyles } from "constants/styles";
import FractionationScanner from "components/WorkOrders/Fractionation/FractionationScanner";
import { useFractionationOrders } from "providers/FractionationOrdersProvider";

export default function FractionationPage() {
  const [tab, setTab] = useState<WorkOrderOperatorStatus>(
    WORK_ORDER_STATUS.PENDING,
  );
  const { orders, loading, reloadOrders } = useFractionationOrders();
  const navigate = useNavigate();

  if (loading) {
    return (
      <PageLayout
        pageTitle="Fraccionado"
        pageDesc="Órdenes de trabajo disponibles"
      >
        <Typography role="status" textAlign="center" py={4}>
          Cargando órdenes…
        </Typography>
        <FractionationScanner />
      </PageLayout>
    );
  }

  if (!orders) {
    return (
      <PageLayout
        pageTitle="Fraccionado"
        pageDesc="Órdenes de trabajo disponibles"
      >
        <Button onClick={() => void reloadOrders()}>Reintentar</Button>
        <FractionationScanner />
      </PageLayout>
    );
  }

  const visible = orders.filter((order) => order.status === tab);
  const openOrder = (order: IMovementWorkOrderListItem) => {
    navigate(ROUTES.FRACTIONATION_DETAIL.replace(":id", String(order.id)));
  };

  return (
    <PageLayout
      pageTitle="Fraccionado"
      pageDesc="Órdenes de trabajo disponibles"
    >
      <Tabs
        value={tab}
        onChange={(_, status: WorkOrderOperatorStatus) => setTab(status)}
        variant="fullWidth"
        sx={{
          position: "sticky",
          top: "4rem", // appBar height
          zIndex: 1,
          bgcolor: "background.paper",
          borderBottom: 1,
          borderColor: "divider",
          minHeight: 48,
          "& .MuiTabs-indicator": { bgcolor: `${STATUS_COLOR[tab]}.main` },
        }}
      >
        {OPERATOR_TABS.map(({ status, label }) => {
          const count = orders.filter(
            (order) => order.status === status,
          ).length;
          const color = STATUS_COLOR[status];
          return (
            <Tab
              key={status}
              value={status}
              label={`${label} (${count})`}
              sx={{
                minHeight: 48,
                textTransform: "none",
                fontWeight: 600,
                overflow: "visible",
                "&.Mui-selected": { color: `${color}.main` },
              }}
            />
          );
        })}
      </Tabs>
      {visible.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={4}>
          No hay órdenes en este estado.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            flex: 1,
            maxWidth: "100%",
            overflowY: "auto",
            px: 2,
            pt: 1.5,
            pb: 2,
            gap: 1.25,
          }}
        >
          {visible.map((order) => (
            <Card key={order.id} variant="outlined">
              <CardActionArea sx={{ p: 2 }} onClick={() => openOrder(order)}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  spacing={1}
                >
                  <Typography fontWeight={700}>Orden #{order.id}</Typography>
                  <Chip
                    size="small"
                    label={WORK_ORDER_STATUS_LABEL[order.status]}
                    color={STATUS_COLOR[order.status]}
                  />
                </Stack>

                {/* Remito */}
                <Typography
                  sx={{ ...typographyStyles.monospace, fontWeight: 500 }}
                >
                  Remito: {order.movement.client_document || "Sin documento"}
                </Typography>

                {/* transport */}
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={0.5}
                  sx={{ mt: 0.25 }}
                >
                  <MdLocalShipping
                    size={14}
                    style={{ opacity: 0.6, flexShrink: 0 }}
                  />
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {order.movement.associated_client || "Cliente sin nombre"}
                  </Typography>
                </Stack>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      )}
      <FractionationScanner />
    </PageLayout>
  );
}
